// v4 (spec-v4-structure-and-stage.md section 10): the web's copy of core's NextStep contract (the web doesn't import
// core), the parser for GET /api/projects/:id/next, and what a tap on the step does on the project screen.
// Keep the types in step with product/core/src/nextstep.ts.

export type NextStepId =
  | "fix_failed"
  | "finish_brief"
  | "write_script"
  | "make_sheet"
  | "approve_sheet"
  | "review"
  | "update_older_looks"
  | "suno_waiting"
  | "produce"
  | "qc_redo"
  | "render"
  | "busy"
  | "watch"
  | "none";

/** Something the user must see (core `Notice`): costs, errors and blockers never sit inside a fold. */
export type NoticeData = { tone: "error" | "warning" | "info"; text: string; code?: string };

export type NextStepData = {
  id: NextStepId;
  /** the button ("Make Pip's sheet · 45"); empty when there's nothing to press */
  label: string;
  /** the Director's sentence for it */
  spoken: string;
  action: { tool: string; args: Record<string, unknown> };
  credits?: number;
  blocking?: NoticeData;
  projectId?: string;
};

const IDS: NextStepId[] = ["fix_failed", "finish_brief", "write_script", "make_sheet", "approve_sheet", "review", "update_older_looks", "suno_waiting", "produce", "qc_redo", "render", "busy", "watch", "none"];
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" ? v : "");

/** `{next: {...}}` or the step itself; null when it isn't one. */
export function normNextStep(raw: unknown): NextStepData | null {
  const o = isObj(raw) && isObj(raw.next) ? raw.next : raw;
  if (!isObj(o) || !IDS.includes(o.id as NextStepId)) return null;
  const a = isObj(o.action) ? o.action : {};
  const b = isObj(o.blocking) && str(o.blocking.text) ? o.blocking : null;
  const tone = b && ["error", "warning", "info"].includes(str(b.tone)) ? (b.tone as NoticeData["tone"]) : "info";
  return {
    id: o.id as NextStepId,
    label: str(o.label),
    spoken: str(o.spoken),
    action: { tool: str(a.tool), args: isObj(a.args) ? a.args : {} },
    ...(typeof o.credits === "number" ? { credits: o.credits } : {}),
    ...(b ? { blocking: { tone, text: str(b.text), ...(str(b.code) ? { code: str(b.code) } : {}) } } : {}),
    ...(str(o.projectId) ? { projectId: str(o.projectId) } : {}),
  };
}

/** Where a tap goes on the project screen when it isn't a direct action. */
export type ProjectTab = "review" | "scene" | "cast" | "versions" | "brief";

/** What tapping the step does on the project screen (the same thing the Director's tool does). */
export type NextTap =
  | { kind: "script" }
  | { kind: "produce"; through: "frames" | "clips" | "sfx" | "render" }
  | { kind: "approve"; what: "frame" | "clip" | "music"; sceneId?: string }
  | { kind: "retry"; what: "frame" | "clip" | "sounds" | "music"; sceneId?: string; model?: string; modelLabel?: string; credits?: number }
  | { kind: "watch" }
  | { kind: "tab"; tab: ProjectTab }
  /** v4 (V4-C4): open the Character screen (`character` is the script's id or the character's uuid) */
  | { kind: "character"; character: string }
  /** v4 (V4-M1): open the Suno pack (its upload is at the bottom) */
  | { kind: "suno" }
  | { kind: "none" };

const THROUGH = ["frames", "clips", "sfx", "render"] as const;

/**
 * Maps a step to a tap. `sceneIds` are the script's scene ids in order (the tools take 1-based scene numbers).
 * Steps whose tools land later in v4 (sheets, older looks) open the tab that holds them today; the Suno upload opens
 * the Suno pack (V4-M1).
 */
export function nextTap(step: NextStepData | null, sceneIds: string[]): NextTap {
  if (!step || !step.label) return { kind: "none" };
  const args = step.action.args;
  const scene = typeof args.scene === "number" ? sceneIds[args.scene - 1] : undefined;
  const sceneArg = scene ? { sceneId: scene } : {};
  switch (step.action.tool) {
    case "write_script":
      return { kind: "script" };
    case "produce": {
      const t = THROUGH.find((x) => x === args.through);
      return t ? { kind: "produce", through: t } : { kind: "none" };
    }
    case "approve": {
      const what = args.what === "clip" || args.what === "music" ? args.what : "frame";
      return what !== "music" && !scene ? { kind: "tab", tab: "review" } : { kind: "approve", what, ...sceneArg };
    }
    case "show_choices":
      return { kind: "tab", tab: "review" };
    case "make_frame":
    case "make_clip":
    case "make_sounds":
    case "make_music": {
      const what = step.action.tool === "make_frame" ? "frame" : step.action.tool === "make_clip" ? "clip" : step.action.tool === "make_sounds" ? "sounds" : "music";
      if (what !== "music" && !scene) return { kind: "none" };
      const model = typeof args.model === "string" ? args.model : undefined;
      const label = /Try (.+)\?/.exec(step.label)?.[1];
      return { kind: "retry", what, ...sceneArg, ...(model ? { model } : {}), ...(model && label ? { modelLabel: label } : {}), ...(step.credits ? { credits: step.credits } : {}) };
    }
    case "play_and_wait":
      return { kind: "watch" };
    case "update_brief":
      return { kind: "tab", tab: "brief" };
    case "approve_sheet":
      return typeof args.character === "string" && args.character ? { kind: "character", character: args.character } : { kind: "tab", tab: "cast" };
    case "make_character_sheet":
    case "update_older_looks":
      return { kind: "tab", tab: "cast" };
    case "add_files":
      return args.origin === "suno" ? { kind: "suno" } : { kind: "tab", tab: "brief" };
    default:
      return { kind: "none" };
  }
}

/**
 * fix-produce: what the next step depends on in the project the screen already has. The step is re-read whenever this
 * changes, so a script that lands (status "Script ready") moves the NEXT bar from "Write the script" on at once, even
 * when the new project came back from a tap (setData) rather than a refresh.
 */
export function nextReloadKey(p: { id: string; updatedAt?: string; status?: string; script?: { scenes?: unknown[] } | null; music?: { status?: string } | null; latestRender?: { id?: string; status?: string } | null } | null): string {
  if (!p) return "";
  return [p.id, p.updatedAt ?? "", p.status ?? "", p.script ? (p.script.scenes?.length ?? 0) : "none", p.music?.status ?? "", p.latestRender?.id ?? "", p.latestRender?.status ?? ""].join("|");
}

/** fix-produce: a job event changes the next step when the job is new or its status moved (queued, running, done, failed). */
export function jobMovesNext(prev: { status: string } | undefined, job: { status: string; projectId?: string | null }): boolean {
  return !!job.projectId && (!prev || prev.status !== job.status);
}
